import { WhereItIs } from "./WhereItIs";
import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScanField } from "@/components/ScanField";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { toast } from "sonner";
import { ScanLine, Box, CheckCircle2, XCircle, PackageCheck, AlertTriangle, Unlink } from "lucide-react";

// Inline pick — the PRIMARY continuous path. You stay in the cart you're already viewing;
// scan part -> scan bin -> confirm, lines flip live, the tracker advances in place.
// Bin-verify is the default; a MISSING bin scan can be overridden (acknowledged + logged)
// only while the admin kill-switch is "override_allowed".
export function InlinePickPanel({ cart, onPicked }) {
  const [gateMode, setGateMode] = useState("override_allowed");
  const [partScan, setPartScan] = useState("");
  const [last, setLast] = useState(null);
  const [ovr, setOvr] = useState(null);          // { code } pending override confirm
  const [ovrReason, setOvrReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/carts/pick-gate").then(({ data }) => setGateMode(data.bin_scan_mode)).catch(() => {}); }, []);
  const focusEl = (tid) => setTimeout(() => { if (!document.querySelector("[id$='-camera-reader'] video")) document.querySelector(`[data-testid="${tid}"]`)?.focus(); }, 60);

  const flash = (ok, payload) => {
    if (ok) scanAccept(payload?.part?.short_code || "PICKED");
    else scanRefuse(payload?.message || "THAT SCAN WAS REFUSED");
    setLast({ ok, ...payload });
  };

  const doPick = useCallback(async (pcode, bcode, override = false, reason = "", meta = null) => {
    if (!pcode) { toast.error("Scan the part first."); return null; }
    setBusy(true);
    try {
      const { data } = await api.post(`/carts/${cart.id}/pick`, {
        part_scan: pcode, location_scan: bcode || undefined,
        bin_override: override, override_reason: reason || undefined,
        provenance: prov("pick_cart", meta),
      });
      if (data.ok) { flash(true, { part: data.part, location: data.location, override: data.bin_override, field_added: data.field_added }); onPicked?.(); }
      else if (data.needs_bin && data.override_available && !override) { setOvr({ code: pcode }); setOvrReason(""); }
      else flash(false, { message: data.message });
      return data;
    } catch (e) { flash(false, { message: formatApiErrorDetail(e.response?.data?.detail) }); return null; }
    finally { setBusy(false); setPartScan(""); }
  }, [cart.id, onPicked]);

  // ONE CAMERA FOR BOTH STEPS: with the camera on the part box, the scan after a part is that part's bin.
  const onPartScan = (code, meta) => {
    if (meta?.input === "camera" && partScan) return onBinScan(code, meta);
    setPartScan(code); focusEl("inline-pick-bin-input");
  };
  const onBinScan = (code, meta) => { doPick(partScan, code, false, "", meta); };

  const confirmOverride = async () => {
    const code = ovr.code; setOvr(null);
    await doPick(code, "", true, ovrReason.trim());
  };

  return (
    <div data-testid="inline-pick-panel" className="border-2 border-[#B01E28]/20 bg-white rounded-xl p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <PackageCheck className="w-4 h-4 text-[#B01E28]" />
        <h3 className="font-display font-bold text-sm uppercase tracking-wide text-slate-700">Pick these parts — you're in the cart, keep moving</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1"><ScanLine className="w-3.5 h-3.5 text-[#B01E28]" />1 · Scan part</Label>
          {partScan && <div data-testid="inline-pick-part-captured" className="text-xs font-mono text-emerald-700 mt-1">✓ {partScan}</div>}
          <div className="mt-1"><ScanField testid="inline-pick-part" autoFocus={false} submitLabel="Next" placeholder="Scan part tag" onScan={onPartScan} continuous paused={!!ovr} /></div>
        </div>
        <div>
          <Label className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1"><Box className="w-3.5 h-3.5 text-[#B01E28]" />2 · Scan bin</Label>
          <div className="mt-1"><ScanField testid="inline-pick-bin" autoFocus={false} submitLabel="Pick" placeholder="Scan bin code" onScan={onBinScan} /></div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <Button data-testid="inline-pick-confirm" disabled={busy || !partScan} onClick={() => doPick(partScan, "")} className="rounded-lg h-10 bg-[#E0781A] hover:bg-[#C76714] text-white">Confirm pick</Button>
        {gateMode !== "hard" && (
          <Button data-testid="inline-pick-override" variant="outline" disabled={busy || !partScan}
            onClick={() => { setOvr({ code: partScan }); setOvrReason(""); }}
            className="rounded-lg h-10 border-amber-300 text-amber-700 hover:bg-amber-50">
            <Unlink className="w-4 h-4 mr-1.5" />Pick without bin scan (logged)
          </Button>
        )}
        {gateMode === "hard" && <span className="text-[11px] text-slate-400">Bin scan required (hard-enforced)</span>}
      </div>

      {last && (
        <div data-testid="inline-pick-flash" className={`border-2 rounded-lg p-3 mt-3 flex items-center gap-3 ${last.ok ? (last.override ? "border-amber-400 bg-amber-50" : "border-emerald-500 bg-emerald-50") : "border-red-500 bg-red-50"}`}>
          {last.ok ? (last.override ? <AlertTriangle className="w-8 h-8 text-amber-600 shrink-0" /> : <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />) : <XCircle className="w-8 h-8 text-red-600 shrink-0" />}
          {last.ok ? (
            <div className="min-w-0">
              <div className="font-display font-black">{last.part?.short_code}</div>
              <div className="text-xs text-slate-600 truncate">{last.part?.name}</div>
              <div className="text-[11px] mt-0.5">{last.override ? <span className="font-mono">PICKED · NO BIN (logged)</span>
                : last.location ? <span className="inline-flex items-center gap-1 flex-wrap"><span className="font-mono">PICKED →</span><WhereItIs location={last.location} size="sm" testid="inline-pick-where" /></span>
                  : <span className="font-mono">PICKED · field-added (no bin)</span>}</div>
            </div>
          ) : <div className="font-mono text-sm text-red-700">{last.message}</div>}
        </div>
      )}

      {/* Acknowledged override — deliberate tap, logged who/when/why (reason optional) */}
      <Dialog open={!!ovr} onOpenChange={(o) => { if (!o) setOvr(null); }}>
        <DialogContent data-testid="inline-pick-override-dialog" className="rounded-xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Unlink className="w-5 h-5 text-amber-600" />Pick without a bin scan?</DialogTitle>
            <DialogDescription>This part isn't confirmed to a bin. This is allowed during load-in but it's <b>logged</b> (who · when) against your name. An admin can switch this off once the yard is put away.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500">Reason (optional)</Label>
            <Input data-testid="inline-pick-override-reason" value={ovrReason} onChange={(e) => setOvrReason(e.target.value)} placeholder="Default: not binned yet — only note if unusual" className="rounded-lg mt-1" />
          </div>
          <DialogFooter>
            <Button data-testid="inline-pick-override-confirm" onClick={confirmOverride} className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white">Pick without bin — I acknowledge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
